import React, { useState, useEffect, useContext } from "react";
import { FormattedMessage } from "react-intl";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Sheet from "@amzn/meridian/sheet";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Tooltip from "@amzn/meridian/tooltip";
import Icon from "@amzn/meridian/icon";
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDashboardConfiguration,
  saveDashboardConfiguration,
} from "../thunks/DashboardConfigurationDispatcher";
import Dashboard from "./Dashboard";
import WidgetSelectionSheet from "../../components/WidgetSelectionSheet";
import {
  CONFIG_GROUP_TYPES,
  TOAST_NOTIFICATION_LEVELS,
  TOAST_MESSAGES,
  TOAST_NOTIFICATION_ACTIONS,
  MY_SCC,
  CUSTOMIZABLE_DASHBOARD_TOOLTIP,
  LAST_UPDATED,
  SAVE,
  EDIT,
  ADD_WIDGETS_TOOLTIP,
  ADD,
  HIGH_PRIORITY_DASHBOARD,
  HIGH_IMPORTANCE_METRICS_TOOLTIP,
  SCOPE,
  DASHBOARD,
  USER_CUSTOMIZABLE_WIDGETS_TOOLTIP,
} from "../../constants/Constants";
import { TOGGLE_EDIT_MODE } from "../reducers/IsEditModeReducer";
import CycleFilter from "../../../overview/CycleFilterv3";
import ToasterComponent from "../../components/ToasterComponent";
import {
  useToastNotification,
  ToastNotificationContext,
} from "../context/ToastNotificationContext";

import { CycleFilterContext } from "../../../overview/context/CycleFilterContext";
import {
  buildMetricFilters,
  getWCCSSafeCapability,
  forceFetchAllMetrics,
  fetchUnfetchedMetrics,
} from "./DashboardUtilities";
import { getStationCode } from "../../../network/NetworkUtil";
import { DEFAULT_REFRESH_INTERVAL } from "../../../outboundAmzl/util/Constants";
import { fetchWorkItemAlerts } from "../thunks/WorkItemAlertsDispatcher";
import { addAlertInfoToMetrics } from "../../util/AlertingUtils";
import Divider from "@amzn/meridian/divider";
import RolesDropdown from "../../components/RolesDropdown";

const DashboardContainer = ({ pageSelected }) => {
  // Dispatcher
  const dispatch = useDispatch();

  // States
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filters, setFilters] = useState(null);
  const [stationType, setStationType] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const selectedRoles = useSelector((state) => state.selectedRoles);
  // TODO add drop down selectors for this
  const viewTypeSelected = "DEFAULT";

  // Context and selectors
  const dashboardConfig = useSelector((state) => state.unSavedDashboardConfig);
  const priorityDashboardConfig = useSelector(
    (state) => state.priorityDashboardConfig,
  );
  const workItemAlerts = useSelector((state) => state.workItemAlerts);
  const isEditing = useSelector((state) => state.isEditMode);
  const allMetricData = useSelector((state) => state.metricData);
  const { cycles } = useContext(CycleFilterContext);

  const { sendToastMessage } = useToastNotification();
  const { toastState, dispatch: toastDispatch } = useContext(
    ToastNotificationContext,
  );

  const handleCloseToast = (toastId) => {
    toastDispatch({
      type: TOAST_NOTIFICATION_ACTIONS.REMOVE_TOAST,
      payload: toastId,
    });
  };

  // Alerting logic
  useEffect(() => {
    if (!workItemAlerts || !allMetricData) return;
    addAlertInfoToMetrics(allMetricData, workItemAlerts, dispatch);
  }, [workItemAlerts, allMetricData]);

  // Fetch Dashboard config from WCSS
  useEffect(() => {
    if (!stationType) return;
    dispatch(
      fetchDashboardConfiguration(
        stationType,
        CONFIG_GROUP_TYPES.USER_ALIAS,
        pageSelected,
        [viewTypeSelected],
      ),
    );
  }, [stationType, pageSelected, viewTypeSelected]);

  // Fetch priority dashboard config from WCSS when roles change
  useEffect(() => {
    if (!stationType) return;
    dispatch(
      fetchDashboardConfiguration(
        stationType,
        CONFIG_GROUP_TYPES.PRIORITY,
        pageSelected,
        selectedRoles,
      ),
    );
  }, [stationType, pageSelected, selectedRoles]);

  // Build filters for metrics
  useEffect(() => {
    setFilters(buildMetricFilters(getStationCode(), cycles.selectedCycles));
  }, [cycles]);

  // Dispatch requests to fetch metric data
  useEffect(() => {
    fetchUnfetchedMetrics(
      [dashboardConfig, priorityDashboardConfig],
      filters,
      allMetricData,
      dispatch,
      setLastUpdated,
    );
  }, [dashboardConfig, allMetricData]);

  // Force refresh metric data when these dependencies change
  useEffect(() => {
    forceFetchAllMetrics(
      [dashboardConfig, priorityDashboardConfig],
      filters,
      dispatch,
      setLastUpdated,
    );
    dispatch(fetchWorkItemAlerts());
  }, [filters]);

  // Periodic fetch every metric every refresh period
  useEffect(() => {
    if (!dashboardConfig.widgets || !filters) return;

    const intervalId = setInterval(() => {
      forceFetchAllMetrics(
        [dashboardConfig, priorityDashboardConfig],
        filters,
        dispatch,
        setLastUpdated,
      );
    }, DEFAULT_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [dashboardConfig.widgets, filters]);

  useEffect(() => {
    getWCCSSafeCapability().then(setStationType);
  }, []);

  const onClickEditButton = () => {
    if (isEditing) {
      sendToastMessage(
        "mySCC.toast.dashboardSaved",
        TOAST_MESSAGES.DASHBOARD_SAVED,
        TOAST_NOTIFICATION_LEVELS.SUCCESS,
      );
      dispatch(
        saveDashboardConfiguration(
          stationType,
          CONFIG_GROUP_TYPES.USER_ALIAS,
          pageSelected,
          viewTypeSelected,
        ),
      );
    }
    dispatch({ type: TOGGLE_EDIT_MODE });
  };

  // TODO: make strings international
  return (
    <Row alignmentVertical="top" width="100%" spacingInset="400" spacing="200">
      {/* Main Content Column */}
      <Column width="100%" height="100%">
        {/* Inline Header - Dashboard title and show metrics buttons */}
        <Row alignmentHorizontal="justify" width="100%">
          {/* Left side - Dashboard title with tooltip */}
          <Column spacing="none">
            <Row spacing="small" alignmentVertical="center">
              <Text type="h400">
                <FormattedMessage
                  id="mySCC.dashboard.title"
                  defaultMessage={MY_SCC}
                />
              </Text>
              <Tooltip
                position="bottom"
                title={
                  <FormattedMessage
                    id="mySCC.dashboard.customizableTooltip"
                    defaultMessage={CUSTOMIZABLE_DASHBOARD_TOOLTIP}
                  />
                }
              >
                <Icon tokens={infoKnockoutTokens} />
              </Tooltip>

              {/* Cycle drop down*/}
              <CycleFilter />
            </Row>
            {lastUpdated && (
              <Text type="b100">
                <FormattedMessage
                  id="mySCC.dashboard.lastUpdated"
                  defaultMessage={LAST_UPDATED}
                />{" "}
                {lastUpdated.toLocaleString()}
              </Text>
            )}
          </Column>

          {/* Right side - Dashboard editing buttons */}
          <Row spacing="small" alignmentVertical="center">
            <Button onClick={onClickEditButton}>
              {isEditing ? (
                <FormattedMessage
                  id="mySCC.dashboard.save"
                  defaultMessage={SAVE}
                />
              ) : (
                <FormattedMessage
                  id="mySCC.dashboard.edit"
                  defaultMessage={EDIT}
                />
              )}
            </Button>
            {/* Right side - Show Metrics Icon Button */}
            <Tooltip
              position="bottom"
              title={
                <FormattedMessage
                  id="mySCC.dashboard.addWidgetsTooltip"
                  defaultMessage={ADD_WIDGETS_TOOLTIP}
                />
              }
            >
              <Button
                type="primary"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                label="Add Widgets"
              >
                <FormattedMessage
                  id="mySCC.dashboard.add"
                  defaultMessage={ADD}
                />
              </Button>
            </Tooltip>
          </Row>
        </Row>

        {/* High Priority Widgets Section */}
        <Box width="100%">
          <Row
            widths={["fit", "fill"]}
            spacing="600"
            alignmentVertical="center"
          >
            <Row spacing="small" alignmentVertical="center">
              <Text type="h300">
                <FormattedMessage
                  id="mySCC.dashboard.highPriorityDashboard"
                  defaultMessage={HIGH_PRIORITY_DASHBOARD}
                />
              </Text>
              <Tooltip
                position="bottom"
                title={
                  <FormattedMessage
                    id="mySCC.dashboard.highImportanceMetricsTooltip"
                    defaultMessage={HIGH_IMPORTANCE_METRICS_TOOLTIP}
                  />
                }
              >
                <Icon tokens={infoKnockoutTokens} />
              </Tooltip>
            </Row>

            <Row alignmentVertical="center">
              <Text type="h100">
                <FormattedMessage
                  id="mySCC.dashboard.scope"
                  defaultMessage={SCOPE}
                />
              </Text>
              <RolesDropdown />
            </Row>
          </Row>
        </Box>
        <Box width="100%" height="100%">
          <Dashboard
            stationType={stationType}
            isPriorityDashboard={true}
            pageSelected={pageSelected}
            viewTypeSelected={viewTypeSelected}
          />
        </Box>

        <Divider />

        {/* Dashboard Section */}
        <Box width="100%">
          <Row spacing="small" alignmentVertical="center">
            <Text type="h300">
              <FormattedMessage
                id="mySCC.dashboard.dashboard"
                defaultMessage={DASHBOARD}
              />
            </Text>
            <Tooltip
              position="bottom"
              title={
                <FormattedMessage
                  id="mySCC.dashboard.userCustomizableWidgetsTooltip"
                  defaultMessage={USER_CUSTOMIZABLE_WIDGETS_TOOLTIP}
                />
              }
            >
              <Icon tokens={infoKnockoutTokens} />
            </Tooltip>
          </Row>
        </Box>
        <Box width="100%" height="100%">
          <Dashboard
            stationType={stationType}
            pageSelected={pageSelected}
            viewTypeSelected={viewTypeSelected}
          />
        </Box>
      </Column>

      {/* Sheet Column */}
      <Column height="100%" spacingInset="none">
        <Sheet
          type="overlay"
          side="right"
          open={sidebarOpen}
          spacingInset="small"
        >
          <WidgetSelectionSheet
            onClose={() => setSidebarOpen(false)}
            stationType={stationType}
            pageSelected={pageSelected}
            viewTypeSelected={viewTypeSelected}
          />
        </Sheet>
      </Column>

      {/* Toaster for notifications */}
      <ToasterComponent
        toasts={toastState.toasts}
        onCloseToast={handleCloseToast}
      />
    </Row>
  );
};

export default DashboardContainer;



// WEBPACK FOOTER //
// ./src/components/mySCC/common/Dashboard/DashboardContainer.js